import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";
import Decimal from "decimal.js";
import CarrierAutocomplete from "../../../../../Inputs/CarrierAutocomplete/CarrierAutocomplete";
import PrimaryTextField from "../../../../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimaryButton from "../../../../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../../../Buttons/SecondaryButton/SecondaryButton";
import StagedComchecksTable from "../StagedComchecksTable/StagedComchecksTable";
import { nullCheck } from "../../../../../../utils/dataHelpers";
import { formatCurrency } from "../../../../../../utils/formatHelpers";
import { CarrierService } from "../../../../../../services/CarrierService";
import { UserPermissionsContext, AlertContext } from "../../../../../Dashboard/Dashboard";
import { securityCheck } from "../../../../../../utils/authHelpers";
import { handleAltEnter } from "../../../../../../utils/keyboardShortcutHelpers";
import WarningMessage from "../../../../../WarningMessage/WarningMessage";

export default function ComchecksStepOne({ 
  activeLoad,
  activeUser,
  carrier,
  setCarrier,
  totalFsc,
  accessorials,
  stagedComchecksArray,
  setStagedComchecksArray,
  setStep,
  comcheckToEdit, 
  setComcheckToEdit,
  setComcheckToReview,
  step,
  handleCancel,
  handleDeleteStagedComcheck,
  issuedComchecks,
  getIssuedComchecks,
  isLoading
}) {
  const { instance } = useMsal();
  const userPermissions = useContext(UserPermissionsContext);
  const [selectedReason, setSelectedReason] = useState("Accessorial");
  const [selectedAccessorialId, setSelectedAccessorialId] = useState(""); 
  const [selectedAccessorial, setSelectedAccessorial] = useState({});
  const [tractorNumber, setTractorNumber] = useState("-");
  const [trailerNumber, setTrailerNumber] = useState("-");
  const [driverName, setDriverName] = useState("");
  const [driverNameRequired, setDriverNameRequired] = useState(false);
  const [sealNumber, setSealNumber] = useState("");
  const [sealNumberRequired, setSealNumberRequired] = useState(false);
  const [bolNumber, setBolNumber] = useState("");
  const [requestedAmount, setRequestedAmount] = useState("");
  const [comcheckFee, setComcheckFee] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [maxAllowed, setMaxAllowed] = useState(0);
  const [displayStagedTable, setDisplayStagedTable] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [errorMessages, setErrorMessages] = useState([]);
  const [fuelStaged, setFuelStaged] = useState(false);
  const [fuelPaid, setFuelPaid] = useState(false);
  const [pifStaged, setPifStaged] = useState(false);
  const [pifPaid, setPifPaid] = useState(false);
  const [comcheckFeePercent, setComcheckFeePercent] = useState(0);
  const [overrideRequired, setOverrideRequired] = useState(false);
  const [isOverrideUser, setIsOverrideUser] = useState(false);
  const [fuelCalculations, setFuelCalculations] = useState({});
  const [pifCalculations, setPifCalculations] = useState({});
  const [pifAuthorized, setPifAuthorized] = useState(false);
  const overrideRoles = ["Super Administrator", "Administrator", "Carrier Rep Manager", "Accounts Payable"];
  const accessorialStatuses = ["LOA", "LOAD", "DEL", "DELV"];
  const fuelStatuses = ["LOAD"];
  const pifStatuses = ["DELV"];
  const accessorialLabel = "Accessorial";
  const fuelAdvanceLabel = "Fuel Advance";
  const pifLabel = "Paid in Full";
  const overrideMessage = "Manager override is required";
  const fuelMaxPercent = .4;

  const { warning } = useContext(AlertContext);

  const handleReview = () => {
    const comcheckToStage = {
      reason: selectedReason === accessorialLabel ? selectedAccessorial.shortDescription : selectedReason,
      tractorNumber: tractorNumber,
      trailerNumber: trailerNumber,
      driverName: driverName,
      sealNumber: sealNumber,
      bolNumber: bolNumber,
      requestedAmount: parseFloat(requestedAmount, 2),
      carrierId: carrier.id,
      carrierName: carrier.name,
      loadId: activeLoad.id,
      movementId: activeLoad.movementId,
      comcheckType: selectedReason,
      accessorial: selectedAccessorial,
      accessorialId: selectedAccessorialId,
      comcheckFee: comcheckFee,
      totalAmount: totalAmount,
      maxAllowed: maxAllowed,
      carrierLinehaul: activeLoad.carrierLinehaul,
      totalFsc: totalFsc,
      userId: activeUser.userIdMcld,
      tempId: comcheckToEdit ? comcheckToEdit.tempId : null
    };

    const comchecksArray = [...stagedComchecksArray];
    const index = comchecksArray.findIndex(c => c.tempId === comcheckToEdit.tempId);

    if (index !== -1) {
      comchecksArray.splice(index, 1, comcheckToStage);
    } else {
      if (stagedComchecksArray.length) {
        comcheckToStage.tempId = Math.max(...stagedComchecksArray.map(o => o.tempId)) + 1;
        comchecksArray.push(comcheckToStage);
      } else {
        comcheckToStage.tempId = 1;
        comchecksArray.push(comcheckToStage);
      }
    }

    setStagedComchecksArray(comchecksArray);
    setComcheckToReview(comcheckToStage);
    setStep(2);
  };

  const addError = (message) => {
    if (!errorMessages.find(e => e === message)) {
      setErrorMessages(errorMessages => (
        [...errorMessages, message]
      ));
    }
  };

  const removeError = (message) => {
    if (errorMessages.find(e => e === message)) {
      setErrorMessages(errorMessages => (
        errorMessages.filter(e => e !== message)
      ));
    }
  };

  const getFuelCalculations = (defaultRequestedAmount) => {
    CarrierService.GetComcheckFuelCalc(instance, carrier.id, activeLoad.movementId, defaultRequestedAmount || requestedAmount).then(res => {
      setFuelCalculations(res);
    }).catch(error => {
      console.error(error);
      warning(error);
    });

  };
  
  const getPifCalculations = () => {
    CarrierService.GetComcheckPifCalc(instance, carrier.id, activeLoad.movementId, requestedAmount).then(res => {
      setPifCalculations(res);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  };

  useEffect(() => {
    if (activeUser?.role) {
      setIsOverrideUser(overrideRoles.includes(activeUser.role.roleName));
    }
  }, [activeUser]);

  useEffect(() => {
    if (fuelCalculations.fee) {
      setComcheckFeePercent(fuelCalculations.feePercentage);
      setComcheckFee(fuelCalculations.fee * -1);
      setMaxAllowed(fuelCalculations.maxCutAmount);
    }
  }, [fuelCalculations]);
 
  useEffect(() => {
    if (activeLoad) {
      setTractorNumber(nullCheck(activeLoad, "-", "movements", "0", "carrier_tractor"));
      setTrailerNumber(nullCheck(activeLoad, "-", "movements", "0", "carrier_trailer"));
    }
  }, [activeLoad]);

  useEffect(() => {
    if (comcheckToEdit?.requestedAmount) {
      setRequestedAmount(comcheckToEdit.requestedAmount);
      setMaxAllowed(comcheckToEdit.maxAllowed);
    } else if (selectedReason === accessorialLabel && selectedAccessorial?.amount) {
      setRequestedAmount(selectedAccessorial.amount);
      setMaxAllowed(selectedAccessorial.amount);
    } else if (selectedReason === accessorialLabel && !selectedAccessorial?.amount) {
      setRequestedAmount("");
      setMaxAllowed(0);
    } else if (fuelCalculations.fee && selectedReason === fuelAdvanceLabel) {
      setRequestedAmount(fuelCalculations.maxCutAmount);
      setComcheckFeePercent(fuelCalculations.feePercentage);
      setComcheckFee(fuelCalculations.fee * -1);
      setMaxAllowed(fuelCalculations.maxCutAmount);
    } else if (!fuelCalculations.fee && selectedReason === fuelAdvanceLabel) {
      setRequestedAmount(activeLoad.carrierLinehaul * fuelMaxPercent);
      getFuelCalculations(activeLoad.carrierLinehaul * fuelMaxPercent); 
    } else if (pifCalculations.fee && selectedReason === pifLabel) {
      setRequestedAmount(pifCalculations.amount + pifCalculations.fee);
      setMaxAllowed(pifCalculations.amount + pifCalculations.fee);
    } 
  }, [activeLoad, selectedReason, selectedAccessorial, pifCalculations]);

  useEffect(() => {
    setTotalAmount(requestedAmount && new Decimal(requestedAmount).minus(new Decimal(comcheckFee)).toString());
  }, [comcheckFee, requestedAmount]);

  useEffect(() => {
    if (selectedAccessorialId) {
      const acc = accessorials.find(a => a.id === selectedAccessorialId);
      setSelectedAccessorial(acc);
    }
  }, [selectedAccessorialId]);

  useEffect(() => {
    setDisplayStagedTable(!!((stagedComchecksArray.length === 1 && !comcheckToEdit.tempId) || stagedComchecksArray.length > 1));
  }, [stagedComchecksArray, comcheckToEdit]);

  useEffect(() => {
    if (comcheckToEdit?.tempId) {
      setSelectedReason(comcheckToEdit.comcheckType);
      setTractorNumber(comcheckToEdit.tractorNumber);
      setTrailerNumber(comcheckToEdit.trailerNumber);
      setDriverName(comcheckToEdit.driverName);
      setSealNumber(comcheckToEdit.sealNumber);
      setBolNumber(comcheckToEdit.bolNumber);
      setRequestedAmount(comcheckToEdit.requestedAmount);
      setComcheckFee(comcheckToEdit.comcheckFee);
      setTotalAmount(comcheckToEdit.totalAmount);
      setMaxAllowed(comcheckToEdit.maxAllowed);
      setSelectedAccessorialId(comcheckToEdit.accessorialId);
    }
  }, [comcheckToEdit, accessorials, step]);

  useEffect(() => {
    if (carrier?.id) {
      getIssuedComchecks();
      getPifCalculations();
    }
    setSelectedAccessorialId("");
  }, [carrier]);

  useEffect(() => {
    if (
      requestedAmount <= 0 ||
      !parseInt(requestedAmount) ||
      errorMessages.length ||
      isLoading || 
      !carrier ||
       (selectedReason === accessorialLabel && (!selectedAccessorialId)) ||
       (selectedReason === fuelAdvanceLabel && (!driverName || !sealNumber)) ||
       (selectedReason === pifLabel && (!driverName))
    ) {
      setSubmitDisabled(true);
    } else {
      setSubmitDisabled(false);
    }

  }, [selectedAccessorialId, requestedAmount, isLoading, carrier, driverName, sealNumber, errorMessages]);

  useEffect(() => {
    if (selectedReason === fuelAdvanceLabel) {
      setDriverNameRequired(true);
      setSealNumberRequired(true);
    } else if (selectedReason === pifLabel) {
      setDriverNameRequired(true);
      setSealNumberRequired(false);
    } else {
      setDriverNameRequired(false);
      setSealNumberRequired(false);
    }

    if (selectedReason === accessorialLabel && !accessorialStatuses.includes(activeLoad.brokerageStatus)) {
      addError(`Carrier must have arrived at the first stop to issue an ${accessorialLabel} Comcheck`);
    } else {
      removeError(`Carrier must have arrived at the first stop to issue an ${accessorialLabel} Comcheck`);
    }

    if (selectedReason === fuelAdvanceLabel && !fuelStatuses.includes(activeLoad.brokerageStatus)) {
      addError(`Unable to issue ${fuelAdvanceLabel} for a load not In Transit`);
    } else {
      removeError(`Unable to issue ${fuelAdvanceLabel} for a load not In Transit`);
    }

    if (selectedReason === pifLabel && !pifStatuses.includes(activeLoad.brokerageStatus)) {
      addError(`Load must be delivered to issue a ${pifLabel} Comcheck`);
    } else {
      removeError(`Load must be delivered to issue a ${pifLabel} Comcheck`);
    }

    if (selectedReason === pifLabel && stagedComchecksArray.length && comcheckToEdit.reason !== pifLabel) {
      addError(`Please request other pending Comchecks before requesting a ${pifLabel} Comcheck`);
    } else {
      removeError(`Please request other pending Comchecks before requesting a ${pifLabel} Comcheck`);
    }

    if (stagedComchecksArray.length && stagedComchecksArray[0].reason === pifLabel && selectedReason !== pifLabel) {
      addError(`Cannot issue another Comcheck with a ${pifLabel} request`);
    } else {
      removeError(`Cannot issue another Comcheck with a ${pifLabel} request`);
    }

    if (activeLoad?.settlementDate) {
      addError("Cannot issue a Comcheck for a load already billed");
    } else {
      removeError("Cannot issue a Comcheck for a load already billed");
    }
  }, [selectedReason, activeLoad, stagedComchecksArray]);

  useEffect(() => {
    if (pifPaid && selectedReason === pifLabel) {
      addError(`A ${pifLabel} Comcheck has already been issued for this load`); 
    } else {
      removeError(`A ${pifLabel} Comcheck has already been issued for this load`); 
    }
  }, [pifPaid, selectedReason]);

  useEffect(() => {
    if (fuelPaid && selectedReason === fuelAdvanceLabel) {
      addError(`A ${fuelAdvanceLabel} Comcheck has already been issued for this load`); 
    } else {
      removeError(`A ${fuelAdvanceLabel} Comcheck has already been issued for this load`); 
    }
  }, [fuelPaid, selectedReason]);

  useEffect(() => {
    if (carrier && !isOverrideUser && !carrier.payAdvances && selectedReason === fuelAdvanceLabel) {
      addError(`Carrier is not authorized to receive ${fuelAdvanceLabel}`);
    } else {
      removeError(`Carrier is not authorized to receive ${fuelAdvanceLabel}`);
    }

    if (carrier && !isOverrideUser && !carrier.payAdvances && selectedReason === pifLabel) {
      addError(`Carrier is not authorized to receive ${pifLabel} advances`);
    } else {
      removeError(`Carrier is not authorized to receive ${pifLabel} advances`);
    }

    setOverrideRequired(!!(carrier?.id && !carrier?.payAdvances && (selectedReason === fuelAdvanceLabel || selectedReason === pifLabel)));
  }, [carrier, selectedReason, isOverrideUser]);
  
  useEffect(() => {
    if (selectedReason === fuelAdvanceLabel) {
      setComcheckFee(requestedAmount * comcheckFeePercent || 0);
    } else if (selectedReason === pifLabel) {
      setComcheckFee(pifCalculations.fee);
    } else {
      setComcheckFee(0);
    }
    
    if (requestedAmount > maxAllowed) {
      addError("Requested amount cannot exceed max allowed");
    } else {
      removeError("Requested amount cannot exceed max allowed");
    }
  }, [requestedAmount, maxAllowed]);

  useEffect(() => {
    setFuelPaid(issuedComchecks.find(ic => ic.comcheckType === "FS" || ic.comcheckType === "FAD"));
    setFuelStaged(!!stagedComchecksArray.find(sc => sc.comcheckType === fuelAdvanceLabel));
    setPifPaid(issuedComchecks.find(ic => ic.comcheckType === "PIF"));
    setPifStaged(!!stagedComchecksArray.find(sc => sc.comcheckType === pifLabel));

  }, [stagedComchecksArray, issuedComchecks]);

  useEffect(() => {
    if (userPermissions) {
      setPifAuthorized(securityCheck(userPermissions, "ComchecksPaidInFull", "Create"));
    }
  }, [userPermissions]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      handleAltEnter(e, submitDisabled, handleReview);
    };

    document.addEventListener("keydown", handleKeyDown);

    return function cleanup() {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [submitDisabled]);
  
  return (
    <Box sx={{ marginTop: "20px" }}>
      <Box className="Accessorial-modal-form-row">
        <CarrierAutocomplete
          title="Verify the Carrier"
          titleSize="subhead2.fontSize"
          width="560px"
          carrierId={activeLoad.payeeId}
          selectedOption={carrier} 
          updateSelectedCarrier={setCarrier} 
          error={!carrier} 
        />
      </Box>
      <Box className="Accessorial-modal-form-row" sx={{ display: "flex" }}>
        <Box>
          <Typography variant="subhead2" color="black_solid.60">
            Tractor / Trailer
          </Typography>
          <Typography variant="subhead1" color="black_solid.80" sx={{ margin: "6px 0 0 4px" }}>
            {tractorNumber} / {trailerNumber}
          </Typography>
        </Box>
        <Box sx={{marginLeft: "80px"}}>
          <Typography variant="subhead2" color="black_solid.60">
            Line Haul
          </Typography>
          <Typography variant="subhead1" color="black_solid.80" sx={{ margin: "6px 0 0 4px" }}>
            {formatCurrency(activeLoad.carrierLinehaul, 2)}
          </Typography>
        </Box>
        <Box sx={{marginLeft: "80px"}}>
          <Typography variant="subhead2" color="black_solid.60">
            Fuel Surcharge
          </Typography>
          <Typography variant="subhead1" color="black_solid.80" sx={{ margin: "6px 0 0 4px" }}>
            {formatCurrency(totalFsc, 2)}
          </Typography>
        </Box>
      </Box>
      <Box className="Accessorial-modal-form-row" sx={{ display: "flex", alignItems: "center" }}>
        <Box>
          <FormHelperText sx={{ whiteSpace: "nowrap", fontSize: "subhead2.fontSize" }}>Comcheck Reason*</FormHelperText>
          <Select
            id="comcheck-reason-select"
            value={selectedReason}
            onChange={(event) => setSelectedReason(event.target.value)}
            size="small"
            sx={{
              width: "270px",
              height: "28px",
              fontSize: "subhead2.fontSize",
              color: "black_solid.80"
            }}
            disabled={!!comcheckToEdit.tempId}
            MenuProps={{ PaperProps: { sx: { boxShadow: "elevation1" } } }}
          >
            <MenuItem value={accessorialLabel}>
              {accessorialLabel}
            </MenuItem>
            <MenuItem value={fuelAdvanceLabel} disabled={!!(fuelPaid || fuelStaged)}>
              {fuelPaid ? (
                `${fuelAdvanceLabel} - PAID: ${fuelPaid.express}` 
              ) : (
                fuelAdvanceLabel
              )}
            </MenuItem>
            {pifAuthorized && 
              <MenuItem value={pifLabel} disabled={!!(pifPaid || pifStaged)}> 
                {pifPaid ? (
                  `${pifLabel} - PAID: ${pifPaid.express}` 
                ) : (
                  pifLabel
                )}
              </MenuItem>
            }
          </Select>
        </Box>
        {selectedReason === accessorialLabel && accessorials[0] &&
          <Box sx={{ marginLeft: "20px"}}>
            <FormHelperText sx={{ whiteSpace: "nowrap", fontSize: "subhead2.fontSize" }}>Accessorials*</FormHelperText>
            <Select
              id="accessorial-select"
              value={selectedAccessorialId}
              onChange={(event) => setSelectedAccessorialId(event.target.value)}
              displayEmpty
              size="small"
              sx={{
                width: "270px",
                height: "38px",
                fontSize: "subhead2.fontSize",
                color: "black_solid.80"
              }}
              MenuProps={{ PaperProps: { sx: { boxShadow: "elevation1" } } }}
            >
              {accessorials.map((accessorial, i) => (
                accessorial.comcheckIssued ? (
                  <MenuItem key={i} value={accessorial.id} disabled>
                    {`${accessorial.shortDescription} - PAID: ${accessorial.express}`}
                  </MenuItem>
                ) : (
                  <MenuItem key={i} value={accessorial.id} disabled={accessorial.stagedComcheck}>
                    {accessorial.shortDescription}
                  </MenuItem>
                )
              ))}
            </Select>
          </Box>
        }
        {overrideRequired && 
          <Typography variant="subhead1" color="error.60" sx={{ margin: "24px 0 0 20px" }}>
            {overrideMessage}
          </Typography>
        }
      </Box>
      <Box className="Accessorial-modal-form-row">
        <PrimaryTextField
          id="comchecks-step-one-driver-name"
          label={driverNameRequired ? "Driver Name*" : "Driver Name"}
          onChange={setDriverName}
          width="560px"
          value={driverName}
          maxLength="32"
          error={driverNameRequired && !driverName}
        />
      </Box>
      <Box className="Accessorial-modal-form-row" sx={{ display: "flex" }}>
        <Box>
          <PrimaryTextField
            id="comchecks-step-one-seal-number"
            label={sealNumberRequired ? "Seal Number*" : "Seal Number"}
            onChange={setSealNumber}
            width="270px"
            fontSize="body1.fontSize"
            value={sealNumber}
            maxLength="32"
            error={sealNumberRequired && !sealNumber}
          />
        </Box>
        <Box sx={{ marginLeft: "20px"}}>
          <PrimaryTextField
            id="comchecks-step-one-bol-numnber"
            label="BOL Number"
            onChange={setBolNumber}
            width="270px"
            value={bolNumber}
            maxLength="32"
          />
        </Box>
      </Box>
      <Box className="Accessorial-modal-form-row" sx={{ display: "flex", alignItems: "center" }}>
        <Box>
          <PrimaryTextField
            id="comchecks-step-one-requested-amount"
            label="Requested Amount*"
            onChange={setRequestedAmount}
            width="130px"
            value={requestedAmount}
            maxLength="9"
            isCurrency
            error={requestedAmount <= 0 || !parseInt(requestedAmount) || requestedAmount > maxAllowed}
            disabled={selectedReason === pifLabel}
            onBlur={() => selectedReason === fuelAdvanceLabel && getFuelCalculations()}
          />
        </Box>
        <Box sx={{ marginLeft: "30px"}}>
          <Typography variant="subhead2" color="black_solid.60">
            Comcheck Fee:
          </Typography>
          <Typography variant="subhead1" color="black_solid.80" sx={{ margin: "5px 0 0 4px" }}>
            {formatCurrency(comcheckFee, 2)}
          </Typography>
        </Box>
        <Box sx={{ marginLeft: "30px"}}>
          <Typography variant="subhead2" color="black_solid.60">
            {`${selectedReason} Total:`}
          </Typography>
          <Typography variant="subhead1" color="black_solid.80" sx={{ margin: "5px 0 0 4px" }}>
            {formatCurrency(totalAmount, 2)}
          </Typography>
        </Box>
        <Box sx={{ marginLeft: "30px"}}>
          <Typography variant="subhead2" color="black_solid.60">
            Max Allowed:
          </Typography>
          <Typography variant="subhead1" color="black_solid.80" sx={{ margin: "5px 0 0 4px" }}>
            {formatCurrency(maxAllowed, 2)}
          </Typography>
        </Box>
      </Box>
      {displayStagedTable &&
        <Box sx={{ margin: "30px 0" }}>
          <StagedComchecksTable 
            activeLoad={activeLoad} 
            stagedComchecksArray={stagedComchecksArray}
            setComcheckToEdit={setComcheckToEdit}
            comcheckIdUnderReview={comcheckToEdit.tempId}
            setStep={setStep}
            handleDeleteStagedComcheck={handleDeleteStagedComcheck}
          />
        </Box>
      }
      <Box sx={{ display: "flex", marginTop: "30px", maxWidth: "810px" }}>
        <Box>
          {errorMessages.map((error, i) => (
            <Box key={`error-${i}`} sx={{ display: "flex", alignItems: "flex-start", margin: "0 5px 5px 0" }}>
              <WarningMessage key={error} text={error} displayText={error} />
            </Box>
          ))}
        </Box>
        <Box sx={{ margin: "0 30px 0 auto"}}>
          <SecondaryButton
            id="cancel-button"
            label="Cancel"
            onClick={handleCancel}
          />
        </Box>
        <Box>
          <PrimaryButton
            id="review-button"
            label="Review"
            onClick={handleReview}
            disabled={submitDisabled}
          />
        </Box>
      </Box>
    </Box>
  );
}

ComchecksStepOne.propTypes = {
  activeLoad: PropTypes.object.isRequired,
  activeUser: PropTypes.object.isRequired,
  carrier: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string
  ]),
  setCarrier: PropTypes.func.isRequired,
  totalFsc: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  accessorials: PropTypes.array,
  setStep: PropTypes.func,
  stagedComchecksArray: PropTypes.array.isRequired,
  setStagedComchecksArray: PropTypes.func,
  comcheckToEdit: PropTypes.object,
  setComcheckToEdit: PropTypes.func,
  setComcheckToReview: PropTypes.func,
  step: PropTypes.number,
  handleCancel: PropTypes.func.isRequired,
  handleDeleteStagedComcheck: PropTypes.func,
  issuedComchecks: PropTypes.array.isRequired,
  getIssuedComchecks: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};